<template>
  <div class="media-preview">
    <!-- Loading state -->
    <div v-if="isMediaLoading && !media.length" class="text-center py-3">
      <CSpinner color="primary" size="sm"/>
      <span class="ml-2 text-muted">
        <translate translate-context="edge-orders.preview">Loading photos...</translate>
      </span>
    </div>

    <!-- No media state -->
    <div v-else-if="!media.length && !hasMedia" class="text-center py-3 text-muted">
      <CIcon name="cipImage" size="lg" class="mr-2"/>
      <translate translate-context="edge-orders.preview">No photos available</translate>
    </div>

    <!-- Context Menu -->
    <PContextMenu ref="contextMenu" :menu-items="contextItems" @item-selected="onContextMenuSelected"/>

    <!-- Media grid preview -->
    <div class="media-preview-container">
      <div class="media-preview-wrapper" :class="{ 'expanded': isExpanded, 'auto-expanded': shouldAutoExpand }">
        <!-- Floating expand button -->
        <div v-if="canExpand && !isExpanded" class="media-preview-expand-floating">
          <CButton
            color="primary"
            size="sm"
            @click="toggleExpanded"
            class="shadow"
          >
            <CIcon name="cipArrowNarrowDown" size="sm" class="mr-1"/>
            <translate translate-context="edge-orders.preview"
                      :translate-params="{count: totalMediaCount}">
              Show all %{count} photos
            </translate>
          </CButton>
        </div>

        <div class="media-preview-grid">
          <div
            v-for="mediaItem in displayedMedia"
            :key="mediaItem.id"
            class="media-preview-item"
          :class="{
            'selected': isMediaSelected(mediaItem.id),
            'hovering': isMediaHovering(mediaItem.id)
          }"
          @click="handleItemClick($event, mediaItem)"
          @contextmenu.prevent="handleContextMenu($event, mediaItem)"
          @mouseenter="handleMouseEnter(mediaItem.id)"
          @mouseleave="handleMouseLeave()"
        >
          <!-- Selection checkbox -->
          <div v-if="allowSelection" class="media-preview-checkbox">
            <CIcon
              name="cipCheckbox"
              size="custom-size"
              :width="20"
              :class="['checkbox-icon', {'selected': isMediaSelected(mediaItem.id)}]"
              @click.stop="handleToggleSelection(mediaItem.id)"
            />
          </div>

          <!-- Print copies indicator (only if media has print info) -->
          <div v-if="mediaItem.print" class="media-preview-copies-badge">
            <CBadge :color="getCopiesBadgeColor(mediaItem.print)" shape="pill">
              <CIcon name="cilPrint" size="sm"/>
            </CBadge>
          </div>

          <!-- Thumbnail -->
          <img
            :src="mediaItem.thumb"
            :alt="`Photo ${mediaItem.id}`"
            @error="onImageError"
            @contextmenu.prevent
            @dragstart.prevent
            class="media-preview-image"
          />

          <!-- Zoom button -->
          <CLink
            v-if="mediaItem.original || mediaItem.sd || mediaItem.hd"
            class="media-preview-zoom-btn"
            @click.stop="zoomMedia(mediaItem)"
            :title="$pgettext('edge-orders.preview', 'View full size')"
          >
            <CIcon name="cipResize" size="custom-size" :width="18" class="text-primary"/>
          </CLink>

          </div>
        </div>

        <!-- Fade overlay when collapsed and more photos available -->
        <div v-if="!isExpanded && !shouldAutoExpand && canExpand" class="media-preview-fade">
          <div class="fade-gradient"></div>
        </div>

        <!-- Floating collapse button when expanded -->
        <div v-if="canExpand && isExpanded" class="media-preview-collapse-floating">
          <CButton
            color="secondary"
            variant="outline"
            size="sm"
            @click="toggleExpanded"
            class="shadow-sm"
          >
            <CIcon name="cipArrowNarrowUp" size="sm" class="mr-1"/>
            <translate translate-context="edge-orders.preview">Show less</translate>
          </CButton>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import PContextMenu from '@/domain/core/components/PContextMenu.vue'

export default {
  name: 'OrderMediaPreview',
  components: {
    PContextMenu
  },
  props: {
    orderItem: {
      type: Object,
      required: true
    },
    order: {
      type: Object,
      required: true
    },
    previewRows: {
      type: Number,
      default: 2
    },
    itemsPerRow: {
      type: Number,
      default: 5
    },
    // Interface methods
    mediaGetter: {
      type: Function,
      required: true
    },
    selectedMediaGetter: {
      type: Function,
      required: true
    },
    loadingGetter: {
      type: Function,
      required: true
    },
    fetchMediaAction: {
      type: Function,
      required: true
    },
    toggleSelectionAction: {
      type: Function,
      required: true
    },
    // Context menu items available for this order item
    contextItems: {
      type: Array,
      default: () => []
      // Array format: [{ value: 'hide', label: 'Hide', icon: 'cilTrash' }, {divider: true}]
    },
    // Whether media are selectable (defaults to true)
    allowSelection: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isExpanded: false,
      currentPage: 1,
      downloading: false,
      calculatedItemsPerRow: this.itemsPerRow,
      lastSelectedId: null,
      shiftPressed: false,
      hoveringMediaIds: {}
    }
  },
  computed: {
    hasMedia() {
      return (this.orderItem.media_ids && this.orderItem.media_ids.length > 0) ||
             (this.orderItem.gallery_ids && this.orderItem.gallery_ids.length > 0)
    },

    mediaData() {
      return this.mediaGetter(this.orderItem.id)
    },

    media() {
      return this.mediaData.media || []
    },

    mediaPagination() {
      return this.mediaData.pagination || null
    },

    selectedMediaIds() {
      return this.selectedMediaGetter(this.orderItem.id)
    },

    isMediaLoading() {
      return this.loadingGetter(this.orderItem.id)
    },

    hasMoreMedia() {
      return this.mediaPagination?.next || false
    },

    shouldAutoExpand() {
      const totalMedia = this.mediaPagination?.total || this.media.length
      const maxVisibleInTwoRows = this.calculatedItemsPerRow * 2
      return totalMedia <= maxVisibleInTwoRows
    },

    displayedMedia() {
      if (this.isExpanded || this.shouldAutoExpand) {
        return this.media
      }
      const itemsToShow = this.calculatedItemsPerRow * 2
      return this.media.slice(0, itemsToShow)
    },

    visibleMedia() {
      return this.displayedMedia
    },

    remainingCount() {
      const total = this.mediaPagination?.total || this.media.length
      return Math.max(0, total - this.visibleMedia.length)
    },

    totalMediaCount() {
      return this.mediaPagination?.total || this.media.length
    },

    canExpand() {
      if (this.shouldAutoExpand) return false
      const itemsInTwoRows = this.calculatedItemsPerRow * 2
      return this.media.length > itemsInTwoRows || this.hasMoreMedia
    }
  },

  methods: {
    onKeyDown(event) {
      if (event.key === 'Shift' && !this.shiftPressed) {
        this.shiftPressed = true
      }
    },

    onKeyUp(event) {
      if (event.key === 'Shift' && this.shiftPressed) {
        this.shiftPressed = false
        this.hoveringMediaIds = {}
      }
    },

    handleMouseEnter(mediaId) {
      if (!this.shiftPressed || !this.lastSelectedId) {
        this.hoveringMediaIds = {}
        return
      }

      const lastIndex = this.media.findIndex(m => m.id === this.lastSelectedId)
      const currentIndex = this.media.findIndex(m => m.id === mediaId)

      if (lastIndex === -1 || currentIndex === -1) {
        this.hoveringMediaIds = {}
        return
      }

      const start = Math.min(lastIndex, currentIndex)
      const end = Math.max(lastIndex, currentIndex)

      const newHovering = {}
      for (let i = start; i <= end; i++) {
        newHovering[this.media[i].id] = true
      }
      this.hoveringMediaIds = newHovering
    },

    handleMouseLeave() {
      // Don't clear on mouse leave
    },

    isMediaHovering(mediaId) {
      return this.hoveringMediaIds[mediaId] === true
    },

    handleItemClick(event, mediaItem) {
      // If selection is not allowed, clicking on the image should open zoom directly
      if (!this.allowSelection) {
        this.zoomMedia(mediaItem)
        return
      }

      // Otherwise, handle selection
      this.handlePhotoClick(event, mediaItem.id)
    },

    handlePhotoClick(event, mediaId) {
      if (!this.allowSelection) return

      if (event.shiftKey || this.shiftPressed) {
        this.handleShiftClick(mediaId)
      } else {
        this.handleToggleSelection(mediaId)
        this.lastSelectedId = mediaId
      }
    },

    handleShiftClick(mediaId) {
      if (!this.lastSelectedId) {
        this.handleToggleSelection(mediaId)
        this.lastSelectedId = mediaId
        return
      }

      const lastIndex = this.media.findIndex(m => m.id === this.lastSelectedId)
      const currentIndex = this.media.findIndex(m => m.id === mediaId)

      if (lastIndex === -1 || currentIndex === -1) {
        this.handleToggleSelection(mediaId)
        this.lastSelectedId = mediaId
        return
      }

      const start = Math.min(lastIndex, currentIndex)
      const end = Math.max(lastIndex, currentIndex)

      const selectedIds = new Set(this.selectedMediaIds)

      for (let i = start; i <= end; i++) {
        selectedIds.add(this.media[i].id)
      }

      this.$emit('bulk-select', {
        orderItemId: this.orderItem.id,
        selectedIds: Array.from(selectedIds)
      })

      this.hoveringMediaIds = {}
    },

    calculateItemsPerRow() {
      if (!this.$el) return

      const container = this.$el.querySelector('.media-preview-grid')
      if (!container) return

      const containerWidth = container.offsetWidth
      const gap = 10
      let itemWidth = 150

      if (window.innerWidth > 1200) {
        itemWidth = 160
      } else if (window.innerWidth < 768) {
        itemWidth = 120
      } else if (window.innerWidth < 576) {
        itemWidth = 100
      }

      this.calculatedItemsPerRow = Math.floor((containerWidth + gap) / (itemWidth + gap))
    },

    isMediaSelected(mediaId) {
      return this.selectedMediaIds.includes(mediaId)
    },

    getCopiesBadgeColor(printInfo) {
      if (!printInfo) return 'light'
      if (printInfo.printed === 0) return 'light'
      if (printInfo.printed < printInfo.total) return 'warning'
      return 'success'
    },

    handleToggleSelection(mediaId) {
      this.toggleSelectionAction({
        orderItemId: this.orderItem.id,
        mediaId
      })
    },

    async handleContextMenu(event, mediaItem) {
      if (!this.allowSelection) return
      if (this.contextItems.length === 0) return

      // If right-clicking on an unselected item and we have other selections, select only this one
      if (!this.isMediaSelected(mediaItem.id) && this.selectedMediaIds.length > 0) {
        this.handleToggleSelection(mediaItem.id)
      } else if (!this.isMediaSelected(mediaItem.id)) {
        // If no selections, select the clicked item
        this.handleToggleSelection(mediaItem.id)
      }

      await this.$nextTick()
      this.$refs.contextMenu.open(event)
    },

    onContextMenuSelected(value) {
      this.$emit('context-action', value)
    },

    async toggleExpanded() {
      this.isExpanded = !this.isExpanded

      if (this.isExpanded && this.hasMoreMedia) {
        while (this.hasMoreMedia) {
          await this.loadMore()
        }
      }
    },

    async loadMedia(page = 1) {
      try {
        await this.fetchMediaAction({
          orderItemId: this.orderItem.id,
          orderItem: this.orderItem,
          picaCode: this.orderItem.picacode || this.order.picacode,
          page
        })
        this.currentPage = page
      } catch (error) {
        console.error('Failed to load media:', error)
        this.notifyError(this.$pgettext('edge-orders.preview', 'Failed to load photos'))
      }
    },

    async loadMore() {
      if (!this.hasMoreMedia) return
      await this.loadMedia(this.currentPage + 1)
    },

    onImageError(event) {
      event.target.src = '/img/placeholder-image.png'
    },

    zoomMedia(media) {
      this.$emit('zoom', media)
    }
  },
  async mounted() {
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)

    this.$nextTick(() => {
      this.calculateItemsPerRow()
    })
    window.addEventListener('resize', this.calculateItemsPerRow)

    if (this.hasMedia && this.media.length === 0) {
      await this.loadMedia()
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calculateItemsPerRow)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
  }
}
</script>

<style scoped>
.media-preview {
  padding: 0.5rem 0;
}

.media-preview-wrapper {
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.media-preview-wrapper:not(.expanded) {
  max-height: 280px;
}

.media-preview-wrapper.auto-expanded {
  max-height: none;
}

.media-preview-wrapper.expanded {
  max-height: none;
}

.media-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  padding-bottom: 20px;
}

.media-preview-fade {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  pointer-events: none;
  z-index: 5;
}

.fade-gradient {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 0.95) 100%
  );
}

@media (min-width: 1200px) {
  .media-preview-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
  .media-preview-wrapper:not(.expanded) {
    max-height: 290px;
  }
}

@media (max-width: 768px) {
  .media-preview-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  .media-preview-wrapper:not(.expanded) {
    max-height: 220px;
  }
}

@media (max-width: 576px) {
  .media-preview-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
  .media-preview-wrapper:not(.expanded) {
    max-height: 190px;
  }
}

.media-preview-item {
  position: relative;
  aspect-ratio: 1;
  border: 2px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.2s;
  background: #f8f9fa;
  cursor: pointer;
}

.media-preview-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.media-preview-item:hover .media-preview-checkbox,
.media-preview-item.selected .media-preview-checkbox {
  display: block;
}

@media (max-width: 992px) {
  .media-preview-checkbox {
    display: block !important;
  }
}

.media-preview-item.selected {
  border-color: #007bff;
  background: #e7f3ff;
  transform: scale(0.95);
}

.media-preview-item.hovering {
  border-color: #007bff;
  background: #cce5ff;
  opacity: 0.7;
  transform: scale(0.97);
}

.media-preview-item.hovering.selected {
  background: #e7f3ff;
  opacity: 1;
  transform: scale(0.95);
}

.media-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-preview-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  display: none;
  cursor: pointer;
}

.checkbox-icon {
  display: block;
  cursor: pointer;
  transition: all 0.2s;
}

.checkbox-icon .box-check {
  stroke: #333;
  fill: rgba(255, 255, 255, 0.9);
}

.checkbox-icon .line-check {
  display: none;
}

.checkbox-icon.selected .box-check {
  fill: #007bff;
  stroke: #007bff;
}

.checkbox-icon.selected .line-check {
  display: block;
}

.media-preview-copies-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.media-preview-zoom-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  z-index: 2;
}

.media-preview-zoom-btn:hover {
  background: white;
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.media-preview-expand-floating {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.media-preview-collapse-floating {
  position: sticky;
  bottom: 20px;
  display: flex;
  justify-content: center;
  margin-top: -40px;
  z-index: 10;
  pointer-events: none;
}

.media-preview-collapse-floating .btn {
  pointer-events: auto;
}
</style>
